import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Trash2, TriangleAlert } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { Stat } from "~/pages/manifold-nextgen/components/Stat";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { nextgenAdminConversationPath } from "~/pages/manifold-nextgen/paths";
import { EVAL_SCORE_MAX, JUDGE_MODEL_KEYS, type EvalRun } from "~/services/evals";
import { DeleteRunDialog } from "./DeleteRunDialog";
import { EvalTable, SortableHead } from "./EvalTable";
import { RunComparisonCard } from "./RunComparisonCard";
import { RunReportCard } from "./RunReportCard";
import { useTableSort } from "./TableSort";
import { ALL_FILTER } from "./constants/runs";
import { CaseOutcome } from "./constants/scoring";
import { RESULT_SORT_DIRECTIONS, ResultSortKey } from "./constants/sorting";
import { RunAgentAvatar } from "./primitives/RunAgentAvatar";
import { OutcomeBadge, RunStatusBadge } from "./primitives/badges";
import { TagChips } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration, formatEvalTimestamp, shortModelName } from "./primitives/format";
import { Fact, Spec, SpecRow } from "./primitives/layout";
import {
  parseApiTimestamp,
  parseTriggeredBy,
  resultDurationSeconds,
  runDurationSeconds,
} from "./selectors/core";
import { buildResultRows, sortResultRows } from "./selectors/results";
import { summarizeRun } from "./selectors/runs";
import {
  useEvalCases,
  useEvalRun,
  useEvalRunResults,
  useEvalRuns,
  useRunAgentLabels,
} from "./useEvalApi";

const RunTab = {
  OVERVIEW: "overview",
  CASES: "cases",
  BASELINE: "baseline",
  HARNESS: "harness",
} as const;
type RunTab = (typeof RunTab)[keyof typeof RunTab];

const OUTCOME_FILTER_OPTIONS = [
  { value: ALL_FILTER, label: "All outcomes" },
  { value: CaseOutcome.PASSED, label: "Passed" },
  { value: CaseOutcome.FAILED, label: "Failed" },
  { value: CaseOutcome.ERRORED, label: "Errored" },
] as const;

interface RunDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string | null;
  /** Called once the run is deleted, so the list behind the drawer can refetch. */
  onDeleted: () => void;
}

/**
 * One run in a drawer: what it was configured to do and how it scored on one tab,
 * every case it executed on the other. Laid out like the case drawer — identity in
 * the header, tabs under it — so the two read as the same kind of panel.
 *
 * Deleting is the only destructive action on this surface, and the API answers 403
 * without `manage:agents`, so the button is withheld from a caller who holds only
 * the route's own `admin:agents`.
 */
export const RunDetailSheet = ({ open, onOpenChange, runId, onDeleted }: RunDetailSheetProps) => {
  const { data: run, error, isLoading } = useEvalRun(runId);
  const canDelete = useCanManageAIAgents();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-2xl">
        {isLoading && !run ? (
          <>
            <SheetHeader className="pr-12">
              <SheetTitle className="sr-only">Eval run</SheetTitle>
              <Skeleton className="h-5 w-48" />
              <SheetDescription asChild>
                <Skeleton className="h-4 w-64" />
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col gap-3 p-4" role="status" aria-label="Loading eval run">
              <Skeleton className="h-16 rounded-lg" />
              <Skeleton className="h-40 rounded-lg" />
            </div>
          </>
        ) : error || !run ? (
          <>
            <SheetHeader className="pr-12">
              <SheetTitle className="sr-only">Eval run</SheetTitle>
              <SheetDescription className="sr-only">The run could not be loaded.</SheetDescription>
            </SheetHeader>
            <ResourceLoadError error={error} resourceLabel="eval run" className="py-16" />
          </>
        ) : (
          // Keyed by run so one run's filters never carry over to the next one
          // opened in the same drawer.
          <RunDrawerBody key={run.id} run={run} />
        )}

        {run && canDelete && (
          <>
            <SheetFooter className="border-t border-border">
              <Button
                variant="outline"
                className="w-fit text-destructive hover:text-destructive"
                onClick={() => setConfirmingDelete(true)}
              >
                <Trash2 className="size-4" aria-hidden />
                Delete
              </Button>
            </SheetFooter>
            <DeleteRunDialog
              run={confirmingDelete ? run : null}
              onOpenChange={setConfirmingDelete}
              onDeleted={() => {
                onDeleted();
                onOpenChange(false);
              }}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

/** The hairline separator between the header's facts. */
const Dot = () => (
  <span className="text-muted-foreground/50" aria-hidden>
    &middot;
  </span>
);

const RunDrawerBody = ({ run }: { run: EvalRun }) => {
  const [tab, setTab] = useState<RunTab>(RunTab.OVERVIEW);
  const { data: resultsPage, isLoading: resultsLoading } = useEvalRunResults(run.id);
  // Unfiltered: a run's cases can carry any tags, so naming them takes the whole list.
  const { data: casesPage } = useEvalCases();
  // Also unfiltered — a baseline can be any earlier run, including one whose tags
  // do not match this one's.
  const { data: runsPage } = useEvalRuns();
  const allRuns = runsPage?.items ?? [];
  // Every candidate baseline's agent too, so the comparison can name the pair.
  const agentLabels = useRunAgentLabels([run.agent_id, ...allRuns.map((item) => item.agent_id)]);

  const results = resultsPage?.items ?? [];
  const summary = summarizeRun(results);
  const { trigger, user } = parseTriggeredBy(run.triggered_by);
  // A tab is offered only when it has something to open onto: a comparison needs
  // an earlier run, and the report is a blob some runs never recorded.
  const hasEarlierRun = allRuns.some(
    (item) =>
      item.id !== run.id && parseApiTimestamp(item.started_at) < parseApiTimestamp(run.started_at)
  );
  const hasHarnessReport = Boolean(run.report?.html);
  const runErrorMessage = typeof run.report?.error === "string" ? run.report.error : null;

  return (
    <>
      <SheetHeader className="pr-12">
        <SheetTitle className="flex items-center gap-2 text-base">
          {formatEvalTimestamp(parseApiTimestamp(run.started_at))}
          <RunStatusBadge status={run.status} />
        </SheetTitle>
        <SheetDescription asChild>
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <TagChips tags={run.tags} emptyLabel="All cases" />
              <Dot />
              {run.agent_id ? (
                <span
                  className={cn(
                    "flex items-center gap-1.5",
                    agentLabels.isNamed(run.agent_id) && "font-medium text-foreground"
                  )}
                  title={run.agent_id}
                >
                  <RunAgentAvatar agentId={run.agent_id} agentLabels={agentLabels} size={20} />
                  {agentLabels.agentLabel(run.agent_id)}
                </span>
              ) : (
                <span>No agent recorded</span>
              )}
              <Dot />
              {run.model_name ? (
                <span className="font-mono" title={run.model_name}>
                  {shortModelName(run.model_name)}
                </span>
              ) : (
                <span>Model not yet recorded</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {(trigger || user) && (
                <>
                  <span>
                    {trigger}
                    {trigger && user && ", "}
                    {user}
                  </span>
                  <Dot />
                </>
              )}
              <span className="flex items-center gap-1">
                <span className="max-w-[13ch] truncate font-mono">{run.id}</span>
                <CopyButton text={run.id} variant="ghost" size="sm" />
              </span>
            </div>
          </div>
        </SheetDescription>
      </SheetHeader>

      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as RunTab)}
        className="min-h-0 flex-1 gap-0"
      >
        <div className="mx-4 mt-2">
          <TabsList>
            <TabsTrigger value={RunTab.OVERVIEW}>Overview</TabsTrigger>
            <TabsTrigger value={RunTab.CASES}>Cases</TabsTrigger>
            {hasEarlierRun && <TabsTrigger value={RunTab.BASELINE}>Baseline</TabsTrigger>}
            {hasHarnessReport && <TabsTrigger value={RunTab.HARNESS}>Harness report</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value={RunTab.OVERVIEW} className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-6">
            {runErrorMessage && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                  <TriangleAlert className="size-4" aria-hidden />
                  The run failed
                </h2>
                <p className="mt-1 text-xs break-words text-red-700">{runErrorMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                label="Mean score"
                value={summary.meanScore == null ? "—" : summary.meanScore.toFixed(2)}
                unit={summary.meanScore == null ? undefined : ` / ${EVAL_SCORE_MAX}`}
              />
              <Stat
                label="Pass rate"
                value={summary.passRate == null ? "—" : `${Math.round(summary.passRate * 100)}%`}
              />
              <Stat
                label="Cases"
                value={String(summary.distinctCaseCount)}
                detail={
                  summary.resultCount > summary.distinctCaseCount
                    ? `${summary.resultCount} iterations`
                    : "Once each"
                }
              />
              <Stat label="Duration" value={formatDuration(runDurationSeconds(run))} />
            </div>

            <div>
              <h3 className="text-xs font-medium text-muted-foreground">Launch parameters</h3>
              <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-4">
                <Fact label="Iterations">
                  <Value>
                    {run.run_parameters.iterations != null
                      ? `${run.run_parameters.iterations} per case`
                      : "Not recorded"}
                  </Value>
                </Fact>
                <Fact label="Concurrency">
                  <Value>
                    {run.run_parameters.max_concurrency != null
                      ? `${run.run_parameters.max_concurrency} cases`
                      : "Not recorded"}
                  </Value>
                </Fact>
                <Fact label="Query rewriting">
                  <Value>{run.run_parameters.rewrite_queries ? "On" : "Off"}</Value>
                </Fact>
                <Fact label="Conversations kept">
                  <Value>
                    {run.run_parameters.keep_conversations ? "Yes" : "No — discarded after judging"}
                  </Value>
                </Fact>
              </div>
            </div>

            <JudgedBy run={run} />
          </div>
        </TabsContent>

        <TabsContent value={RunTab.CASES} className="min-h-0 flex-1 overflow-y-auto p-4">
          <CaseResults
            results={results}
            cases={casesPage?.items ?? []}
            isLoading={resultsLoading}
          />
        </TabsContent>

        <TabsContent value={RunTab.BASELINE} className="min-h-0 flex-1 overflow-y-auto p-4">
          <RunComparisonCard
            bare
            run={run}
            agentLabels={agentLabels}
            results={results}
            cases={casesPage?.items ?? []}
            allRuns={allRuns}
          />
        </TabsContent>

        <TabsContent value={RunTab.HARNESS} className="min-h-0 flex-1 overflow-y-auto p-4">
          <RunReportCard run={run} defaultOpen />
        </TabsContent>
      </Tabs>
    </>
  );
};

const Value = ({ children }: { children: React.ReactNode }) => (
  <span className="block text-sm tabular-nums text-foreground">{children}</span>
);

const JudgedBy = ({ run }: { run: EvalRun }) => {
  const judgeModels = JUDGE_MODEL_KEYS.map((entry) => ({
    ...entry,
    model: run.judge_config?.[entry.key] ?? null,
  })).filter((entry) => entry.model != null);

  if (judgeModels.length === 0 && !run.case_created_by) {
    return null;
  }

  return (
    <div>
      <h3 className="text-xs font-medium text-muted-foreground">Judged by</h3>
      <Spec className="mt-2">
        {judgeModels.map((entry) => (
          <SpecRow key={entry.key} label={entry.label}>
            <span className="font-mono text-xs" title={entry.model ?? undefined}>
              {shortModelName(entry.model ?? "")}
            </span>
          </SpecRow>
        ))}
        {run.case_created_by && (
          <SpecRow label="Cases authored by">
            <Value>{run.case_created_by}</Value>
          </SpecRow>
        )}
      </Spec>
    </div>
  );
};

const CaseResults = ({
  results,
  cases,
  isLoading,
}: {
  results: Parameters<typeof buildResultRows>[0];
  cases: Parameters<typeof buildResultRows>[1];
  isLoading: boolean;
}) => {
  const [query, setQuery] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>(ALL_FILTER);
  const sort = useTableSort<ResultSortKey>(ResultSortKey.OUTCOME, "asc", RESULT_SORT_DIRECTIONS);

  const search = query.trim().toLowerCase();
  const rows = buildResultRows(results, cases).filter((row) => {
    if (outcomeFilter !== ALL_FILTER && row.outcome !== outcomeFilter) {
      return false;
    }
    return !search || (row.evalCase?.name ?? row.result.case_id).toLowerCase().includes(search);
  });
  const sorted = sortResultRows(rows, sort.sortKey, sort.direction);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search cases…"
          className="flex-1"
          aria-label="Search case results"
        />
        <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
          <SelectTrigger className="w-[150px]" aria-label="Filter by outcome">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OUTCOME_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && results.length === 0 ? (
        <Skeleton className="h-48 rounded-lg" />
      ) : sorted.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {results.length === 0
            ? "This run has posted no results yet."
            : "No case results match this filter."}
        </p>
      ) : (
        <EvalTable>
          <TableHeader>
            <TableRow>
              <SortableHead columnKey={ResultSortKey.CASE} label="Case" sort={sort} />
              <TableHead>Iteration</TableHead>
              <SortableHead columnKey={ResultSortKey.OUTCOME} label="Outcome" sort={sort} />
              <SortableHead columnKey={ResultSortKey.SCORE} label="Score" alignRight sort={sort} />
              <SortableHead
                columnKey={ResultSortKey.DURATION}
                label="Time"
                alignRight
                sort={sort}
              />
              <TableHead>Transcript</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row) => (
              // Nothing in the row is clickable, so it does not take the table's row hover.
              <TableRow key={row.result.id} className="[tbody_&]:hover:bg-transparent">
                <TableCell className="max-w-[220px]">
                  <span
                    className="block truncate font-mono text-xs font-medium"
                    title={row.evalCase?.name ?? row.result.case_id}
                  >
                    {row.evalCase?.name ?? row.result.case_id}
                  </span>
                </TableCell>
                <TableCell className="text-xs tabular-nums text-muted-foreground">
                  {row.siblingResultIds.length > 1
                    ? `${row.result.iteration + 1} of ${row.siblingResultIds.length}`
                    : "Once"}
                </TableCell>
                <TableCell>
                  <OutcomeBadge outcome={row.outcome} />
                </TableCell>
                <TableCell className="text-right text-xs">
                  <ScoreValue score={row.result.score} />
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                  {formatDuration(resultDurationSeconds(row.result))}
                </TableCell>
                <TableCell>
                  {row.result.conversation_id ? (
                    <Link
                      to={nextgenAdminConversationPath(row.result.conversation_id)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      Open
                      <ExternalLink className="size-3" aria-hidden />
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">&mdash;</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </EvalTable>
      )}
    </div>
  );
};
